import type {
  AircraftCatalog,
  ArrivalInquiry,
  PlacementResult,
  PlacementSlot,
  ZoneSlot,
  ZoneSlotGrid,
} from './types'

interface SlotState {
  slot: ZoneSlot
  /** busy intervals [arrival, departure) currently assigned to this slot */
  intervals: Array<[number, number]>
  /** latest departure among assigned intervals (−Infinity if never used) */
  lastEnd: number
}

/** Two half-open intervals [a1,d1) and [a2,d2) overlap iff a1 < d2 && a2 < d1. */
function overlaps(a1: number, d1: number, a2: number, d2: number): boolean {
  return a1 < d2 && a2 < d1
}

function isCompatible(state: SlotState, arr: number, dep: number): boolean {
  for (const [a, d] of state.intervals) {
    if (overlaps(arr, dep, a, d)) return false
  }
  return true
}

/**
 * Time-aware placement: schedule an ordered prefix of inquiries into fixed
 * uniform slots, REUSING a slot once its previous occupant has departed.
 * Capacity becomes peak concurrent occupancy, not total processed — so far more
 * inquiries fit than the straight `runPlacement` fill, which reserves a spot for
 * each aircraft's entire stay.
 *
 * Same input/output contract as `runPlacement` so the store swap is one line.
 * Inquiries are processed in QUEUE ORDER (never re-sorted) — each is assigned
 * against the busy lists built by its predecessors, so the first-K prefix is
 * stable and the result deterministic (re-deriving the same K is idempotent,
 * which is what lets the store preserve `confirmed` flags by scheduleId).
 */
export function scheduleReuse(
  ordered: ArrivalInquiry[],
  grids: ZoneSlotGrid[],
  catalog: AircraftCatalog
): PlacementResult {
  const slots: PlacementSlot[] = []
  const unplaced: string[] = []

  // Per-grid slot state, in fillPriority order (grids are pre-sorted). Slots
  // within a grid keep their (laneIndex, slotIndex) order for deterministic ties.
  const states: SlotState[][] = grids.map((g) =>
    g.slots.map((slot) => ({ slot, intervals: [], lastEnd: Number.NEGATIVE_INFINITY }))
  )

  for (const inq of ordered) {
    const fp = catalog.get(inq.typeCode)?.footprint
    if (!fp) {
      // Unknown footprint — counts as unplaced (fail soft).
      unplaced.push(inq.scheduleId)
      continue
    }

    let chosen: SlotState | null = null

    for (let g = 0; g < grids.length && !chosen; g++) {
      const grid = grids[g]
      // Eligibility size caps (rarely set on KAGS today).
      if (
        (grid.maxWingspanFt && fp.wingspan > grid.maxWingspanFt) ||
        (grid.maxLengthFt && fp.length > grid.maxLengthFt)
      ) {
        continue
      }

      // Footprint on the progression axis must fit the slot box. Uniform slots
      // are sized to the max so this never bites; variable slots (75th pct) reject
      // the oversized minority here, so they spill to a later zone / overflow.
      const footprintAxis = grid.progression === 'side_by_side' ? fp.wingspan : fp.length

      // Among compatible slots, prefer reusing an already-used slot (concentrates
      // occupancy → keeps peak low) whose previous occupant departed latest
      // (tightest temporal fit); otherwise take the tightest-fitting virgin slot
      // (smallest extent that fits) so wing-to-wing variable slots don't drop a
      // small type into an oversized box and leave a gap. Slots are iterated in
      // (laneIndex, slotIndex) order, so equal-size ties keep the lowest index.
      let bestUsed: SlotState | null = null
      let bestVirgin: SlotState | null = null
      for (const st of states[g]) {
        if (footprintAxis > st.slot.extentFt) continue
        if (!isCompatible(st, inq.arrival, inq.departure)) continue
        if (st.intervals.length === 0) {
          if (!bestVirgin || st.slot.extentFt < bestVirgin.slot.extentFt) bestVirgin = st
        } else if (!bestUsed || st.lastEnd > bestUsed.lastEnd) {
          bestUsed = st
        }
      }
      chosen = bestUsed ?? bestVirgin
    }

    if (!chosen) {
      unplaced.push(inq.scheduleId)
      continue
    }

    chosen.intervals.push([inq.arrival, inq.departure])
    if (inq.departure > chosen.lastEnd) chosen.lastEnd = inq.departure
    slots.push({
      scheduleId: inq.scheduleId,
      areaId: chosen.slot.areaId,
      laneIndex: chosen.slot.laneIndex,
      position: chosen.slot.position,
      rotationDeg: chosen.slot.rotationDeg,
    })
  }

  return { slots, unplaced }
}

/**
 * Peak concurrent occupancy across a set of [arrival, departure) intervals — a
 * sweep over start/end events. Departures at an instant are processed before
 * arrivals (half-open semantics: a slot freed exactly at t is reusable at t,
 * matching the scene's `arrival <= t < departure` filter). This is the true
 * capacity metric for time-aware reuse.
 */
export function peakConcurrent(intervals: Array<[number, number]>): number {
  const events: Array<[number, number]> = [] // [time, delta]
  for (const [a, d] of intervals) {
    events.push([a, 1])
    events.push([d, -1])
  }
  // Sort by time; at equal time, departures (−1) before arrivals (+1).
  events.sort((x, y) => x[0] - y[0] || x[1] - y[1])

  let cur = 0
  let peak = 0
  for (const [, delta] of events) {
    cur += delta
    if (cur > peak) peak = cur
  }
  return peak
}
